/**
 * ===========================================================================
 * OPERATING JURISDICTIONS — WHERE THIS ORGANIZATION OPERATES
 * ===========================================================================
 *
 * Every route here is gated `jurisdiction:configure` (migration
 * 1861000000000). That key is NOT `org:manage`: whoever may change the office
 * phone number is not thereby somebody who may decide which state's
 * supervision rule binds a program.
 *
 * WHAT THESE ROUTES ARE NOT. They configure WHERE WE OPERATE. They cannot
 * publish, modify, weaken or suppress an external legal requirement — that is
 * platform authority over `global_knowledge_source`, a separate domain with a
 * separate permission model, and the two-authority ruling of 2026-09-07 keeps
 * them apart on purpose. The only place they meet is `resolve()` below, which
 * asks which external rules apply HERE.
 *
 * ---------------------------------------------------------------------------
 * TWO TIME AXES, AND A CALLER MUST KNOW WHICH ONE IT IS ASKING ABOUT
 * ---------------------------------------------------------------------------
 * `at` is VALID time — over which days was this jurisdiction in force.
 * `knownAsOf` is TRANSACTION time — what did the RECORD say, on the day we
 * asked. Half-open effective dating gives only the first, and on its own an
 * edit today changes what yesterday's answer would have been. `resolve()`
 * without `knownAsOf` answers about the record as it stands now; with it, the
 * answer comes from the ledger and carries `reproducible`.
 *
 * NOTE ON THE NAMESPACE. This module is reached today as
 * `organization.jurisdictions.*`, because `packages/api-client/src/index.ts`
 * is integrator-owned and a lane may not add the top-level export. Promoting
 * it is one line — `export * as jurisdictions from './jurisdictions.js';` —
 * and nothing in this file changes when it happens.
 */
import { get, post } from './transport.js';
/**
 * `GET /jurisdictions` — every recorded operating jurisdiction, CURRENT AND
 * ENDED (needs `jurisdiction:configure`).
 *
 * Ended rows are included deliberately. "We operated in Iowa from 2023 to
 * 2025" is the answer to an audit question, and the caller decides how to show
 * a closed window — it must not render as current.
 */
export async function list(options) {
    const body = await get('/jurisdictions', options);
    return body.jurisdictions;
}
/** `POST /jurisdictions` (needs `jurisdiction:configure`). */
export async function record(input, options) {
    const body = await post('/jurisdictions', input, options);
    return body.jurisdiction;
}
/**
 * `POST /jurisdictions/:id/end` (needs `jurisdiction:configure`).
 *
 * Writes the end date and keeps the row. `effectiveTo` is required and is
 * never defaulted to today: an agency that left in March and is recording it in
 * June must be able to say March, and the difference between the two dates is
 * exactly what the revision ledger keeps visible.
 */
export async function end(id, effectiveTo, options) {
    const body = await post(`/jurisdictions/${id}/end`, { effectiveTo }, options);
    return body.jurisdiction;
}
/** `GET /jurisdictions/revisions` — the ledger (needs `jurisdiction:configure`). */
export async function revisions(jurisdictionId, options) {
    const body = await get('/jurisdictions/revisions', {
        ...options,
        query: jurisdictionId ? { jurisdictionId } : {},
    });
    return body.revisions;
}
/**
 * `GET /jurisdictions/resolve` — which jurisdictions govern this context
 * (needs `jurisdiction:configure`).
 *
 * Most-specific-first: location, service, program, then the organization
 * default — and an organization default NEVER outranks a more specific
 * recorded row. Naming a site or a service type additionally reads
 * `location:read` / `service:read`; without those the dimension comes back
 * `context_refused` rather than silently widening.
 */
export async function resolve(query = {}, options) {
    return get('/jurisdictions/resolve', { ...options, query: { ...query } });
}
//# sourceMappingURL=jurisdictions.js.map